<template>
  <div
    :class="{ visible: shouldShow }"
    class="blocklist-suggestions-popover d-flex flex-column"
  >
    <div v-if="!done" class="d-flex flex-column">
      <div class="d-flex align-items-center justify-content-between i-mb-1">
        <div class="i-mr-2 i-text-subheading-2">Blocklist these details?</div>
        <i-button
          left-icon="small_delete_1"
          :size="iButtonSizes.SMALL"
          :type="iButtonTypes.NEUTRAL"
          :button-style="iButtonStyles.GHOST"
          class="close"
          @button-click="close()"
        />
      </div>
      <small class="i-mb-2">
        This customer has submitted fraudulent documents. Adding their details
        to a blocklist ensures you catch any future applications they make.
        <a
          href="https://help.inscribe.ai/en/articles/3650412-blacklist-database"
          target="_blank"
          >Learn more.</a
        >
      </small>
      <div class="parsed-details d-flex flex-column">
        <div
          v-for="(detail, index) in groupedParsedDetails"
          :key="index"
          class="parsed-name-group text-uppercase i-my-1 d-flex flex-row justify-items-start i-gap-4"
        >
          <i-checkbox
            input-value="checked"
            :value="selectedDetails.includes(index)"
            @input="(value) => onCheck(detail, value)"
          />

          <div
            v-if="selectedDetails.includes(detail)"
            class="d-flex flex-column"
          >
            <i-inline-input
              :value="detail.name.toUpperCase()"
              placeholder="Customer name"
              class="i-p-1"
              @input="$emit('input-text', index, 'name', $event.target.value)"
            />

            <i-inline-input
              :value="detail.address.toUpperCase()"
              placeholder="Customer address"
              class="i-p-1 d-flex"
              @input="
                $emit('input-text', index, 'address', $event.target.value)
              "
            />
          </div>
          <div v-else class="d-flex flex-column i-text-body-2">
            <span class="i-p-1">{{ detail.name }}</span>
            <span class="i-p-1">{{ detail.address }}</span>
          </div>
        </div>
        <i-button
          class="i-mt-2 mr-auto"
          :text="`Blocklist ${
            selectedDetails.length > 0 ? `(${selectedDetails.length})` : ''
          }`"
          size="sm"
          :loading="loading"
          :disabled="selectedDetails.length === 0"
          @button-click="addSelectedItemsToBlocklist()"
        ></i-button>
      </div>
    </div>
    <div v-else>
      <p>The following item(s) were successfully added to the blocklist</p>
      <div class="d-flex flex-column w-100 added-items i-mt-3 i-pl-2">
        <div
          v-for="detail in selectedDetails"
          :key="detail.name"
          class="d-flex flex-column w-100 i-mb-2"
        >
          <span>{{ detail.name }}</span>
          <span>{{ detail.address }}</span>
        </div>
      </div>
      <p class="i-my-2">
        <small
          >You can view and edit the blocklist in
          <router-link :to="`/decision-engine/blocklist`"
            >your settings</router-link
          ></small
        >
      </p>
      <i-button
        class="i-mt-2 mr-auto"
        text="Close"
        size="sm"
        @button-click="close()"
      ></i-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import {
  iButton,
  iCheckbox,
  iInlineInput,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iMenuTriggerAlignOptions,
  positionMenu,
} from '@inscribe/signal';

export default {
  components: {
    iButton,
    iCheckbox,
    iInlineInput,
  },
  props: {
    customer: {
      type: Object,
      required: true,
    },
    groupedParsedDetails: {
      type: Array,
      default: () => [],
      required: true,
    },
    show: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedDetails: [],
      loading: false,
      done: false,
      shouldShow: this.show,
      iMenuTriggerAlignOptions,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
    };
  },

  watch: {
    show: {
      handler: function (val) {
        this.shouldShow = val;
        if (val) {
          this.done = false;
        }
      },
    },
  },
  mounted() {
    this.done = false;
    const triggerButton = document.getElementById('customer-blocklist-trigger');
    triggerButton &&
      positionMenu(triggerButton, this.$el, iMenuTriggerAlignOptions.RIGHT);
  },
  methods: {
    ...mapActions(['tryAddBlocklist']),
    ...mapMutations(['setShowBlocklistSuggestions']),
    close() {
      this.shouldShow = false;
      this.$emit('update:show', false);
      this.setShowBlocklistSuggestions(false);
    },
    onCheck(detail, value) {
      if (this.selectedDetails.includes(detail)) {
        this.selectedDetails = this.selectedDetails.filter(
          (val) => val !== detail,
        );
      } else {
        this.selectedDetails = [...this.selectedDetails, detail];
      }
    },
    addSelectedItemsToBlocklist() {
      if (this.selectedDetails.length > 0) {
        this.loading = true;
        Promise.all(
          this.selectedDetails.map((detail) => this.tryAddBlocklist(detail)),
        )
          .then(() => {
            this.loading = false;
            this.done = true;
          })
          .catch(() => {
            this.loading = false;
          });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.blocklist-suggestions-popover {
  position: absolute;
  padding: 16px;
  width: 250px;
  box-shadow: 0px 1px 20px 0px rgba(95, 95, 95, 0.15);
  background: white;
  visibility: hidden;

  &.visible {
    visibility: visible;
  }

  .close {
    font-size: $font-size-small;
  }

  .parsed-name-group {
    overflow-x: scroll;

    .custom-control.custom-checkbox {
      padding-left: 1.25rem;
    }

    label {
      &::before,
      &::after {
        left: -1.25rem;
        height: 0.75rem;
        top: 0.4rem;
        width: 0.75rem;
      }

      input,
      span {
        font-size: $font-size-small;
        width: 100%;
      }
    }
  }

  .added-items {
    span {
      font-size: $font-size-small;
    }
  }
}
</style>
